import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import { DayPicker } from 'react-day-picker';
import { FaIcon } from '../FaIcon';
import { BusinessUnitPeriodFieldClass } from '../client/BusinessUnitPeriodField';
import BudgetValueInput from './BudgetValueInput';
import { dayPickerLocaleProps, formatShortDate, initialMonthWithin } from '../../utils/localizedDates';
import {
    BudgetHelper,
    DAILY_RANGE_ENTRY_MODE_DAILY,
    DAILY_RANGE_ENTRY_MODE_RANGE,
} from '../../utils/BudgetHelper';
import BudgetPartialGoalsDailyRangeFeedback from './BudgetPartialGoalsDailyRangeFeedback';

const MAX_ENTRIES = 20;

const DEFAULT_ENTRY = {
    mode: DAILY_RANGE_ENTRY_MODE_DAILY,
    date: '',
    date_from: '',
    date_to: '',
    amount: '',
};

class BudgetPartialGoalDailyRangeRowsClass extends Component {
    constructor(props) {
        super(props);
        const hydrated = BudgetHelper.hydrateDailyRangeEntries(props.inputDefinition);
        this.state = {
            entries: hydrated.length > 0 ? hydrated : [{ ...DEFAULT_ENTRY }],
            openPicker: null, // { index, field }
        };
    }

    _emitChange = (entries) => {
        this.props.onChange(entries);
    };

    handleModeChange = (index, mode) => {
        const entries = [...this.state.entries];
        entries[index] = { ...DEFAULT_ENTRY, mode, amount: entries[index].amount };
        this.setState({ entries }, () => this._emitChange(entries));
    };

    handleAmountChange = (index, amount) => {
        const entries = [...this.state.entries];
        entries[index] = { ...entries[index], amount };
        this.setState({ entries }, () => this._emitChange(entries));
    };

    handleDateChange = (index, field, value) => {
        const entries = [...this.state.entries];
        entries[index] = { ...entries[index], [field]: value };
        this.setState({ entries, openPicker: null }, () => this._emitChange(entries));
    };

    addRow = () => {
        if (this.state.entries.length >= MAX_ENTRIES) return;
        const entries = [...this.state.entries, { ...DEFAULT_ENTRY }];
        this.setState({ entries }, () => this._emitChange(entries));
    };

    removeRow = (index) => {
        const entries = [...this.state.entries];
        entries.splice(index, 1);
        const final = entries.length > 0 ? entries : [{ ...DEFAULT_ENTRY }];
        this.setState({ entries: final }, () => this._emitChange(final));
    };

    togglePicker = (index, field) => {
        const { openPicker } = this.state;
        if (openPicker && openPicker.index === index && openPicker.field === field) {
            this.setState({ openPicker: null });
        } else {
            this.setState({ openPicker: { index, field } });
        }
    };

    render() {
        const { t } = this.props;
        const { entries } = this.state;
        const totalRows = entries.length;
        const canAdd = totalRows < MAX_ENTRIES;

        return (
            <div className="budget_partial_goal_rows budget_partial_goal_daily_range">
                {entries.map((entry, index) => this.renderRow(index, entry, totalRows))}
                {canAdd && (
                    <div style={{ marginTop: '0.4em', marginBottom: '0.6em' }}>
                        <button type="button"
                                onClick={this.addRow}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    padding: '0.3em 0.5em',
                                    color: '#FF470F',
                                    fontSize: '0.85em',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.4em',
                                }}
                                aria-label={t('budget_partial_goal_daily_range.add_row')}>
                            <FaIcon iconName="plus-square" />
                            <span>{t('budget_partial_goal_daily_range.add_row')}</span>
                        </button>
                    </div>
                )}
                <BudgetPartialGoalsDailyRangeFeedback
                    totalBudget={this.props.budgetTotalAmount}
                    budgetFromDate={this.props.budgetFromDate}
                    budgetToDate={this.props.budgetToDate}
                    entries={entries}
                />
            </div>
        );
    }

    renderRow(index, entry, totalRows) {
        const { t } = this.props;
        const { openPicker } = this.state;
        const canRemove = totalRows > 1;

        return (
            <div key={index}
                 className="row g-2 align-items-center mb-2 flex-nowrap"
                 style={{ minWidth: 0 }}>
                <div className="col-auto" style={{ width: '1.5em', textAlign: 'right', color: '#4A4A4A' }}>
                    <span>{`${index + 1}.`}</span>
                </div>

                <div className="col-auto">
                    {this.renderModeSelect(index, entry.mode)}
                </div>

                {entry.mode === DAILY_RANGE_ENTRY_MODE_DAILY
                    ? this.renderDailyDateField(index, entry, openPicker)
                    : this.renderRangeDateFields(index, entry, openPicker)}

                <div className="col-auto">
                    <span style={{ color: '#4A4A4A' }}>{t('budget_partial_goal_daily_range.invest_label')}</span>
                </div>

                <div className="col-auto">
                    <BudgetValueInput
                        hideCurrency={true}
                        amountClassName={'value-input'}
                        numberLocale={this.props.numberLocale}
                        readOnly={false}
                        onAmountChange={(newAmount) => this.handleAmountChange(index, newAmount)}
                        budgetAmount={entry.amount}
                    />
                </div>

                {entry.mode === DAILY_RANGE_ENTRY_MODE_RANGE && (
                    <div className="col-auto">
                        <span style={{ color: '#4A4A4A', fontSize: '0.9em' }}>{t('budget_partial_goal_daily_range.per_day')}</span>
                    </div>
                )}

                <div className="col-auto ms-auto">
                    {canRemove && (
                        <button type="button"
                                onClick={() => this.removeRow(index)}
                                className="enabled"
                                aria-label={t('budget_partial_goal_daily_range.remove_row')}
                                style={{ background: 'transparent', border: 'none', color: '#B6B6B7', cursor: 'pointer', padding: '0.2em 0.3em' }}>
                            <FaIcon iconName="minus-square" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    renderModeSelect(index, mode) {
        const { t } = this.props;
        return (
            <select
                className="form-control form-select"
                value={mode}
                onChange={(e) => this.handleModeChange(index, e.target.value)}
            >
                <option value={DAILY_RANGE_ENTRY_MODE_DAILY}>
                    {t('budget_partial_goal_daily_range.mode_daily')}
                </option>
                <option value={DAILY_RANGE_ENTRY_MODE_RANGE}>
                    {t('budget_partial_goal_daily_range.mode_range')}
                </option>
            </select>
        );
    }

    renderDailyDateField(index, entry, openPicker) {
        const { t, budgetPeriodType, budgetFromDate, budgetToDate } = this.props;
        const isOpen = openPicker && openPicker.index === index && openPicker.field === 'date';
        return (
            <>
                <div className="col-md-auto">
                    <span>{t('budget_partial_goal_daily_range.day_label')}</span>
                </div>
                <div className="col-md-auto">
                    {this.renderDateInput(entry.date, (date) => this.handleDateChange(index, 'date', date),
                        budgetPeriodType, budgetFromDate, budgetToDate, isOpen,
                        () => this.togglePicker(index, 'date'))}
                </div>
            </>
        );
    }

    renderRangeDateFields(index, entry, openPicker) {
        const { t, budgetPeriodType, budgetFromDate, budgetToDate } = this.props;
        const isFromOpen = openPicker && openPicker.index === index && openPicker.field === 'date_from';
        const isToOpen = openPicker && openPicker.index === index && openPicker.field === 'date_to';
        return (
            <>
                <div className="col-md-auto">
                    <span>{t('budget_partial_goal_daily_range.range_from')}</span>
                </div>
                <div className="col-md-auto">
                    {this.renderDateInput(entry.date_from, (date) => this.handleDateChange(index, 'date_from', date),
                        budgetPeriodType, budgetFromDate, budgetToDate, isFromOpen,
                        () => this.togglePicker(index, 'date_from'))}
                </div>
                <div className="col-md-auto">
                    <span>{t('budget_partial_goal_daily_range.range_to')}</span>
                </div>
                <div className="col-md-auto">
                    {this.renderDateInput(entry.date_to, (date) => this.handleDateChange(index, 'date_to', date),
                        budgetPeriodType, budgetFromDate, budgetToDate, isToOpen,
                        () => this.togglePicker(index, 'date_to'))}
                </div>
            </>
        );
    }

    renderDateInput(currentDate, onChange, budgetPeriodType, budgetFromDate, budgetToDate, isOpen, onTogglePicker) {
        if (budgetPeriodType === BusinessUnitPeriodFieldClass.temporalPeriodType) {
            return this.renderEventualDatePicker(currentDate, onChange, budgetFromDate, budgetToDate, isOpen, onTogglePicker);
        }
        return this.renderMonthlyDaySelect(currentDate, onChange, budgetFromDate, budgetToDate);
    }

    renderMonthlyDaySelect(currentDate, onChange, budgetFromDate, budgetToDate) {
        // Para DAILY_RANGE permitimos hasta el último día del período (inclusive),
        // a diferencia del modo CUMULATIVE que excluye el último día. El backend
        // genera el anchor en day-1 cuando el spike cae en el último día.
        const lastDayEligible = new Date(budgetToDate);
        const options = this._generateMonthlyOptions(budgetFromDate, lastDayEligible);

        const value = currentDate
            ? `${currentDate.getFullYear()}${String(currentDate.getMonth() + 1).padStart(2, '0')}${String(currentDate.getDate()).padStart(2, '0')}`
            : '';

        return (
            <select
                className="form-control form-select"
                value={value}
                onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                        onChange('');
                        return;
                    }
                    const selected = new Date(
                        v.substring(0, 4),
                        parseInt(v.substring(4, 6), 10) - 1,
                        v.substring(6, 8),
                    );
                    onChange(selected);
                }}
            >
                <option value=""></option>
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        );
    }

    _generateMonthlyOptions(budgetFromDate, lastDayEligible) {
        const options = [];
        let cursor = new Date(budgetFromDate);
        const limit = new Date(lastDayEligible);
        while (cursor <= limit) {
            const year = cursor.getFullYear();
            const month = String(cursor.getMonth() + 1).padStart(2, '0');
            const day = String(cursor.getDate()).padStart(2, '0');
            options.push({ value: `${year}${month}${day}`, label: cursor.getDate() });
            cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
        }
        return options;
    }

    renderEventualDatePicker(currentDate, onChange, budgetFromDate, budgetToDate, isOpen, onTogglePicker) {
        const { t } = this.props;
        const display = currentDate ? this._formatDateDisplay(currentDate) : t('budget_partial_goal.select');
        return (
            <div className="date-picker-container">
                <input
                    type="text"
                    readOnly
                    className="form-control date-input"
                    value={display}
                    onClick={onTogglePicker}
                />
                {isOpen && (
                    <div className="date-picker-popover">
                        <DayPicker
                            {...(this.props.dateLocale ? dayPickerLocaleProps(this.props.dateLocale) : {})}
                            defaultMonth={initialMonthWithin(currentDate, budgetFromDate, budgetToDate)}
                            mode="single"
                            selected={currentDate ? moment(currentDate).toDate() : null}
                            onSelect={(d) => onChange(d)}
                            disabled={(date) => date < moment(budgetFromDate).toDate() || date > moment(budgetToDate).toDate()}
                            numberOfMonths={1}
                        />
                    </div>
                )}
            </div>
        );
    }

    _formatDateDisplay(date) {
        // Con `dateLocale` (el wizard de la UX 2.1) la fecha se lee «31 oct 2026»; sin él, la de siempre.
        if (this.props.dateLocale) return formatShortDate(date, this.props.dateLocale);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${date.getFullYear()}-${m}-${d}`;
    }
}

export const BudgetPartialGoalDailyRangeRows = withTranslation()(BudgetPartialGoalDailyRangeRowsClass);
export default BudgetPartialGoalDailyRangeRows;
